import { useMe } from 'pobble-components/dist/pobble-user'
import format from 'date-fns/format'
import { useEffect, useMemo } from 'react'
import { apiClient } from 'pobble-components/dist/api'

const currencyMap = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: '$',
  GBP: '£'
}

export const useSubscriptionDetails = () => {
  const { me } = useMe()

  return useMemo(() => {
    if (!me) return {}

    /**
     * Relying on the subscription plan to determine if user is a member
     *  of a paid school (Pobble or Moderation)
     */
    let isInPobbleSchool = !!me.account.school_subscriptions.filter(sub => {
      return (
        (sub.plan === 'pobble-school-a' || sub.plan === 'pobble-school-ab') &&
        (sub.status === 'active' ||
          sub.status === 'non_renewing' ||
          sub.status === 'in_trial')
      )
    }).length
    let isInModerationSchool = !!me.account.school_subscriptions.filter(
      sub =>
        (sub.plan === 'pobble-school-b' ||
          sub.plan === 'pobble-school-ab' ||
          sub.plan === 'pobble-school-c' ||
          sub.plan === 'pobble-school-d') &&
        (sub.status === 'active' ||
          sub.status === 'non_renewing' ||
          sub.status === 'in_trial')
    ).length

    let schoolPlanTitle = ''
    if (isInPobbleSchool) {
      schoolPlanTitle = 'Pobble 365'
      if (isInModerationSchool) {
        schoolPlanTitle += ' + Moderation'
      }
    } else if (isInModerationSchool) {
      schoolPlanTitle = 'Moderation'
    }

    /**
     * Determening if user has a moderation add-on.
     */
    const individualModeration =
      me.account.addons && me.account.addons.indexOf('pobble-moderation') !== -1

    /**
     * Prepare a variable for a marketing title of individual plan
     * This title may differ from the name of the variable we use on the backend
     */
    const individualPlan = me.account.individual_subscription.plan
    let individualPlanTitle = ''

    /**
     * Legacy plan names
     * We keep them to show subscription status for
     *  existing customers
     */
    if (individualPlan === 'pobble-bronze') {
      individualPlanTitle = 'Silver'
    } else if (individualPlan === 'pobble-gold') {
      individualPlanTitle = 'For my School'

      /**
       * Up to date plan names
       */
    } else if (individualPlan === 'pobble-silver') {
      individualPlanTitle = 'Pobble 365'
    } else if (individualPlan === 'pobble-custom' && individualModeration) {
      individualPlanTitle = 'Moderation'
    } else {
      individualPlanTitle = 'Free'
    }

    if (individualPlan !== 'pobble-custom' && individualModeration) {
      individualPlanTitle += ' + Moderation'
    }

    const hasAccessToTL =
      isInPobbleSchool ||
      (individualPlan === 'pobble-silver' &&
        me.account.individual_subscription.status !== 'cancelled')

    const hasAccessToPH =
      individualPlan === 'pobble-home' &&
      me.account.individual_subscription.status !== 'cancelled'

    const offerFreeTrial = !me.account.had_trial_before && !individualModeration

    return {
      hadTrialBefore: me.account.had_trial_before,
      offerFreeTrial,
      hasAccessToTL,
      hasAccessToPH,
      isInPobbleSchool,
      isInModerationSchool,
      nonRenewing: me.account.individual_subscription.status === 'non_renewing',
      periodEnd: format(
        new Date(me.account.individual_subscription.period_end),
        'DD-MM-YYYY'
      ),
      individualModeration,
      individualPlanTitle,
      schoolPlanTitle,
      cancelled: me.account.individual_subscription.status === 'cancelled',
      individualPlan,
      annual: me.account.individual_subscription.annual,
      dueInvoice: me.account.individual_subscription.due_invoice,
      paused: me.account.individual_subscription.status === 'paused',
      noSubscription:
        !me.account.individual_subscription.plan &&
        !me.account.teacher_in_pobble_school,
      prices: me.account.subscription_prices,
      paid: me.account.paid,
      freeTrial: me.account.individual_subscription.status === 'in_trial',
      validPaymentMethod:
        me.account.individual_subscription.payment_method_status === 'valid',
      cancelsAtTrialEnd:
        me.account.individual_subscription.cancels_at_trial_end,
      currency: me.account.subscription_currency,
      currencySign: currencyMap[me.account.subscription_currency],
      currentSubsType: !me.account.individual_subscription.plan
        ? null
        : me.account.individual_subscription.annual
        ? 'annual'
        : 'monthly'
    }
  }, [me])
}

let getChargebeeInstance = null

export const useChargebeeScript = () => {
  useEffect(() => {
    if (!document.querySelector('#chargebee-script')) {
      getChargebeeInstance = new Promise(resolve => {
        const script = document.createElement('script')
        script.id = 'chargebee-script'
        script.src = 'https://js.chargebee.com/v2/chargebee.js'
        const siteNamePromise = apiClient.get(
          'v3/subscriptions/chargebee_site_name '
        )
        script.onload = () => {
          siteNamePromise.then(res => {
            res.data.chargebee_site_name
            const chargebeeInstance = window.Chargebee.init({
              site: res.data.chargebee_site_name,
              enableGTMTracking: true
            })
            resolve(chargebeeInstance)
          })
        }
        document.body.append(script)
      })
    }
  }, [])

  return {
    getChargebeeInstance: getChargebeeInstance,
    openCheckout: ({ url, params, options = {} }) => {
      return getChargebeeInstance.then(chargebeeInstance => {
        return chargebeeInstance.openCheckout({
          hostedPage: () => {
            return apiClient
              .get(url, { params })
              .then(res => res.data.hosted_page_object)
          },
          ...options
        })
      })
    }
  }
}
